/* ── Samples experience runtime ─────────────────────────────────────────
   Powers the swatch-grid + filter chips + curated rail + sticky box panel
   on /collections/samples (template_suffix: samples-page).

   Selection state lives on the <sample-page> custom element. Every action
   re-renders the box panel and updates filter chip state from the same
   single source of truth.

   No frameworks. No build step. Vanilla custom element. Mobile uses the
   fixed bottom bar that opens the same panel as a sheet.
   ─────────────────────────────────────────────────────────────────── */

(function() {
  'use strict';

  if (customElements.get('sample-page')) return;

  class SamplePage extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map(); /* id -> { id, title, price, image } */
      this.filters = { range: new Set(), where: new Set() };
      this._raf = null;
    }

    connectedCallback() {
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
      this.cartUrl = this.dataset.cartUrl || '/cart';

      this.gridEl = this.querySelector('[data-grid-target]');
      this.boxListEl = this.querySelector('[data-box-list]');
      this.boxCountEl = this.querySelector('[data-box-count]');
      this.boxTotalEl = this.querySelector('[data-box-total]');
      this.boxCountMobileEl = this.querySelector('[data-box-count-mobile]');
      this.boxTotalMobileEl = this.querySelector('[data-box-total-mobile]');
      this.boxCountBadgeEl = this.querySelector('[data-box-count-badge]');
      this.liveEl = this.querySelector('[data-sample-live]');
      this._barLabelTemplate = this.boxBarEl ? this.boxBarEl.getAttribute('data-box-bar-label-template') : '';
      this.boxCtaEl = this.querySelector('[data-action="send-box"]');
      this.boxClearEl = this.querySelector('[data-action="clear-box"]');
      this.boxBarEl = this.querySelector('[data-box-bar]');
      this.countEl = this.querySelector('[data-count-target]');
      this.resetEl = this.querySelector('[data-action="clear-filters"]');
      this.emptyEl = this.querySelector('[data-empty-target]');

      /* Mobile sheet bindings */
      this.boxSheetEl = this.querySelector('[data-box-sheet]');
      this.boxListSheetEl = this.querySelector('[data-box-list-sheet]');
      this.boxCountSheetEl = this.querySelector('[data-box-count-sheet]');
      this.boxTotalSheetEl = this.querySelector('[data-box-total-sheet]');
      this.boxCtaSheetEl = this.querySelector('[data-action="send-box-sheet"]');
      this.boxClearSheetEl = this.querySelector('[data-action="clear-box-sheet"]');

      this.cards = Array.from(this.querySelectorAll('[data-sample-card]'));
      this.expandCards = Array.from(this.querySelectorAll('[data-sample-expand]'));
      this.totalVariants = this.cards.length + this.expandCards.length;

      this._wireCards();
      this._wireExpand();
      this._wireFilters();
      this._wireBox();
      this._wireRail();
      this._wireFilterPinning();
      this._wireSectionAccordion();
      this._wireMobileSheet();

      this._renderBox();
    }

    /* ── Section accordion (mobile only) ──────────────────────────
       Sections render server-side as .is-open (graceful degradation).
       On mobile load, collapse them all so the page is scannable.
       Tapping a header toggles. */
    _wireSectionAccordion() {
      const isMobile = () => window.matchMedia('(max-width: 820px)').matches;
      const sections = Array.from(this.querySelectorAll('[data-section-target]'));

      const sync = () => {
        sections.forEach(s => {
          const btn = s.querySelector('.sample-section__header');
          if (!btn) return;
          if (isMobile()) {
            s.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
          } else {
            s.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      };

      sync();

      sections.forEach(s => {
        const btn = s.querySelector('.sample-section__header');
        if (!btn) return;
        btn.addEventListener('click', () => {
          if (!isMobile()) return; /* desktop: no-op */
          const open = s.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });

      /* Reapply on viewport resize across the breakpoint */
      let lastIsMobile = isMobile();
      window.addEventListener('resize', () => {
        const m = isMobile();
        if (m !== lastIsMobile) { lastIsMobile = m; sync(); }
      }, { passive: true });
    }

    /* ── Mobile sheet ─────────────────────────────────────────────
       Bottom bar tap → open sheet (not send to cart).
       Sheet shows full selection list + Send my box CTA + Clear.
       Backdrop / close button / Esc dismiss. */
    _wireMobileSheet() {
      if (!this.boxSheetEl) return;

      const closeTriggers = this.boxSheetEl.querySelectorAll('[data-sheet-close]');
      closeTriggers.forEach(el => el.addEventListener('click', () => this._closeSheet()));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._sheetOpen) this._closeSheet();
      });

      if (this.boxCtaSheetEl) {
        this.boxCtaSheetEl.addEventListener('click', () => this._sendBox(this.boxCtaSheetEl));
      }
      if (this.boxClearSheetEl) {
        this.boxClearSheetEl.addEventListener('click', () => {
          this.selected.clear();
          this.querySelectorAll('[data-sample-card][aria-pressed="true"]').forEach(c => {
            c.setAttribute('aria-pressed', 'false');
            c.classList.remove('is-selected');
          });
          this._renderBox();
        });
      }
    }

    _openSheet() {
      if (!this.boxSheetEl) return;
      this.boxSheetEl.hidden = false;
      this.boxSheetEl.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        this.boxSheetEl.classList.add('is-open');
      });
      document.body.classList.add('sample-sheet-open');
      this._sheetOpen = true;
    }

    _closeSheet() {
      if (!this.boxSheetEl) return;
      this.boxSheetEl.classList.remove('is-open');
      this.boxSheetEl.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sample-sheet-open');
      this._sheetOpen = false;
      setTimeout(() => {
        if (!this._sheetOpen) this.boxSheetEl.hidden = true;
      }, 320);
    }

    /* ── Filter pinning: compact when sticky-stuck ────────────────
       Editorial heading + sub line are useful at the natural position
       (a section break between curated and catalogue), but they eat
       vertical real estate once the filter is sticky-pinned. Drop them
       in pinned state via an .is-pinned class. */
    _wireFilterPinning() {
      const filterEl = this.querySelector('.sample-filters');
      if (!filterEl) return;
      if (typeof IntersectionObserver === 'undefined') return;

      /* Sentinel sits 1px above the filter; when it scrolls out of view,
         the filter has just hit its sticky position. Account for the
         filter's own sticky offset (e.g. 80px below header) via rootMargin. */
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;pointer-events:none;';
      filterEl.parentNode.insertBefore(sentinel, filterEl);

      const stickyTopPx = parseInt(getComputedStyle(filterEl).top, 10) || 80;
      const observer = new IntersectionObserver(
        function(entries) {
          filterEl.classList.toggle('is-pinned', !entries[0].isIntersecting);
        },
        {
          threshold: 0,
          rootMargin: '-' + (stickyTopPx + 1) + 'px 0px 0px 0px'
        }
      );
      observer.observe(sentinel);
    }

    /* ── Single-variant cards ─────────────────────────────────────── */
    _wireCards() {
      this.cards.forEach(card => {
        card.addEventListener('click', () => {
          const id = card.dataset.variantId;
          if (this.selected.has(id)) {
            const prev = this.selected.get(id);
            this.selected.delete(id);
            card.setAttribute('aria-pressed', 'false');
            card.classList.remove('is-selected');
            this._renderBox();
            this._announce(`${prev.title} removed from your sample box.`);
          } else {
            const data = {
              id: id,
              title: card.dataset.variantTitle || '',
              price: parseInt(card.dataset.variantPrice, 10) || 0,
              image: card.dataset.variantImage || '',
              range: card.dataset.range || ''
            };
            this.selected.set(id, data);
            card.setAttribute('aria-pressed', 'true');
            card.classList.add('is-selected');
            this._flyToBox(card);
            this._renderBox();
            this._announce(`${data.title} added to your sample box. ${this.selected.size} ${this.selected.size === 1 ? 'sample' : 'samples'} total.`);
          }
        });
      });
    }

    /* ── Expand cards (multi-option) ──────────────────────────────── */
    _wireExpand() {
      this.expandCards.forEach(card => {
        const trigger = card.querySelector('[data-expand-trigger]');
        const expandPanel = card.querySelector('.sample-card__expand');
        const variantsScript = card.querySelector('[data-group-variants]');
        const addBtn = card.querySelector('[data-expand-add]');
        const statusEl = card.querySelector('[data-expand-status]');
        if (!trigger || !expandPanel || !variantsScript) return;

        let groupVariants = [];
        try { groupVariants = JSON.parse(variantsScript.textContent); }
        catch (e) { console.warn('[wvh] expand variants parse failed', e); return; }

        trigger.addEventListener('click', () => {
          const open = card.getAttribute('aria-expanded') === 'true';
          card.setAttribute('aria-expanded', open ? 'false' : 'true');
          expandPanel.hidden = open;
        });

        const optChips = card.querySelectorAll('.sample-card__opt-chip');
        const selection = {}; /* opt index -> chosen value */

        optChips.forEach(chip => {
          chip.addEventListener('click', () => {
            const optEl = chip.closest('.sample-card__opt');
            const optIndex = parseInt(optEl.dataset.optIndex, 10);
            optEl.querySelectorAll('.sample-card__opt-chip').forEach(c => c.setAttribute('aria-checked', 'false'));
            chip.setAttribute('aria-checked', 'true');
            selection[optIndex] = chip.dataset.optValue;
            this._resolveExpandSelection(card, groupVariants, selection, addBtn, statusEl);
          });
        });

        if (addBtn) {
          addBtn.addEventListener('click', () => {
            const matchedId = addBtn.dataset.matchedVariantId;
            if (!matchedId) return;
            if (this.selected.has(matchedId)) return; /* already in box */

            const matchedTitle = addBtn.dataset.matchedTitle || '';
            const matchedPrice = parseInt(addBtn.dataset.matchedPrice, 10) || 0;
            const matchedImage = card.querySelector('.sample-card__media img');

            this.selected.set(matchedId, {
              id: matchedId,
              title: matchedTitle,
              price: matchedPrice,
              image: matchedImage ? matchedImage.src : '',
              range: card.dataset.range || ''
            });
            this._flyToBox(card);
            statusEl.textContent = 'Added to box.';
            this._renderBox();
            /* Reset chip selection so user can add another combo */
            setTimeout(() => {
              card.querySelectorAll('.sample-card__opt-chip').forEach(c => c.setAttribute('aria-checked', 'false'));
              for (const k in selection) delete selection[k];
              addBtn.disabled = true;
              addBtn.removeAttribute('data-matched-variant-id');
              statusEl.textContent = 'Pick the options to add this finish';
            }, 1100);
          });
        }
      });
    }

    _resolveExpandSelection(card, groupVariants, selection, addBtn, statusEl) {
      /* Find matching variant by walking groupVariants, comparing options[i] for each picked index */
      const match = groupVariants.find(v => {
        for (const idx in selection) {
          if (v.options[idx] !== selection[idx]) return false;
        }
        return Object.keys(selection).length > 0;
      });

      const optsTotal = groupVariants[0] && groupVariants[0].options ? groupVariants[0].options.length - 1 : 0;
      const picked = Object.keys(selection).length;

      if (!match || picked < optsTotal) {
        addBtn.disabled = true;
        addBtn.removeAttribute('data-matched-variant-id');
        const remaining = optsTotal - picked;
        statusEl.textContent = remaining > 0
          ? `Pick ${remaining} more option${remaining === 1 ? '' : 's'}`
          : 'No matching combination';
        return;
      }

      addBtn.disabled = false;
      addBtn.dataset.matchedVariantId = match.id;
      addBtn.dataset.matchedTitle = match.title;
      addBtn.dataset.matchedPrice = match.price;
      statusEl.textContent = 'Ready to add.';
    }

    /* ── Filters ──────────────────────────────────────────────────── */
    _wireFilters() {
      const chips = this.querySelectorAll('.sample-chip[data-filter]');
      chips.forEach(chip => {
        chip.addEventListener('click', () => {
          const axis = chip.dataset.filter;
          const value = chip.dataset.value;
          const set = this.filters[axis];
          if (!set) return;
          if (set.has(value)) {
            set.delete(value);
            chip.setAttribute('aria-pressed', 'false');
          } else {
            set.add(value);
            chip.setAttribute('aria-pressed', 'true');
          }
          this._applyFilters();
        });
      });

      const resets = this.querySelectorAll('[data-action="clear-filters"]');
      resets.forEach(btn => btn.addEventListener('click', () => this._clearFilters()));
    }

    _applyFilters() {
      const r = this.filters.range;
      const w = this.filters.where;

      let visible = 0;
      const allCards = [].concat(this.cards, this.expandCards);
      allCards.forEach(card => {
        const cardRange = card.dataset.range || '';
        const cardWhere = (card.dataset.useCase || '').split('|').filter(Boolean);

        const rangeMatch = r.size === 0 || r.has(cardRange);
        const whereMatch = w.size === 0 || cardWhere.some(uc => w.has(uc));

        const show = rangeMatch && whereMatch;
        card.hidden = !show;
        if (show) visible++;
      });

      /* Hide sections that no longer have any visible cards. Updates the
         per-section count too so customers always see what's left in each. */
      this.querySelectorAll('[data-section-target]').forEach(section => {
        const grid = section.querySelector('.sample-section__grid');
        if (!grid) return;
        const sectionCards = grid.querySelectorAll('[data-sample-card], [data-sample-expand]');
        let visibleInSection = 0;
        sectionCards.forEach(c => { if (!c.hidden) visibleInSection++; });
        section.hidden = visibleInSection === 0;
        const countEl = section.querySelector('[data-section-count]');
        if (countEl) {
          countEl.textContent = `${visibleInSection} ${visibleInSection === 1 ? 'finish' : 'finishes'}`;
        }
      });

      if (this.countEl) {
        this.countEl.textContent = `Showing ${visible} ${visible === 1 ? 'finish' : 'finishes'}`;
      }
      if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
      if (this.resetEl) this.resetEl.hidden = (r.size + w.size) === 0;
    }

    _clearFilters() {
      this.filters.range.clear();
      this.filters.where.clear();
      this.querySelectorAll('.sample-chip[data-filter]').forEach(c => c.setAttribute('aria-pressed', 'false'));
      this._applyFilters();
    }

    /* ── Curated rail "Add set" ───────────────────────────────────── */
    _wireRail() {
      const setBtns = this.querySelectorAll('.sample-rail__cta');
      setBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const card = btn.closest('.sample-rail__card');
          if (!card) return;
          const idsRaw = card.dataset.variantIds || '';
          const ids = idsRaw.split(',').map(s => s.trim()).filter(Boolean);
          ids.forEach(id => {
            if (this.selected.has(id)) return;
            const matched = this.querySelector(`[data-sample-card][data-variant-id="${id}"]`);
            if (matched) {
              matched.setAttribute('aria-pressed', 'true');
              matched.classList.add('is-selected');
              this.selected.set(id, {
                id: id,
                title: matched.dataset.variantTitle || '',
                price: parseInt(matched.dataset.variantPrice, 10) || 0,
                image: matched.dataset.variantImage || '',
                range: matched.dataset.range || ''
              });
            } else {
              this.selected.set(id, { id: id, title: '', price: 0, image: '', range: '' });
            }
          });
          this._flyToBox(card);
          this._renderBox();
          this._announce(`Set added to your sample box. ${this.selected.size} ${this.selected.size === 1 ? 'sample' : 'samples'} total.`);
        });
      });
    }

    /* ── Curated rail CTA state ───────────────────────────────────
       On desktop the box panel sits below the fold, so without this the
       click looks like it did nothing. The CTA reads "In your box" while
       every one of the set's variants is selected — synced from box state
       (not a click timer), so removing any member sample reverts it. */
    _syncRailCtas() {
      this.querySelectorAll('.sample-rail__card').forEach(card => {
        const btn = card.querySelector('.sample-rail__cta');
        if (!btn) return;
        const ids = (card.dataset.variantIds || '').split(',').map(s => s.trim()).filter(Boolean);
        const allIn = ids.length > 0 && ids.every(id => this.selected.has(id));
        btn.classList.toggle('is-added', allIn);
        btn.textContent = allIn ? 'In your box' : 'Add set';
      });
    }

    /* ── Box panel render ─────────────────────────────────────────── */
    _renderBox() {
      const items = Array.from(this.selected.values());
      const count = items.length;
      const total = items.reduce((s, i) => s + (i.price || 0), 0);
      const totalStr = this._money(total);

      const rowsHtml = count === 0 ? '' : items.map(i => `
        <div class="sample-box-panel__row" data-row-id="${this._esc(i.id)}">
          <span class="sample-box-panel__thumb">${i.image ? `<img src="${this._esc(i.image)}" alt="" loading="lazy">` : ''}</span>
          <span class="sample-box-panel__row-body">
            <span class="sample-box-panel__row-title">${this._esc(i.title)}</span>
            <span class="sample-box-panel__row-price">${this._money(i.price)}</span>
          </span>
          <button type="button" class="sample-box-panel__row-remove" aria-label="Remove" data-remove-id="${this._esc(i.id)}">×</button>
        </div>
      `).join('');

      /* Desktop right rail list */
      if (this.boxListEl) {
        this.boxListEl.innerHTML = count === 0
          ? '<p class="sample-box-panel__empty" data-empty-state>Empty for now.</p>'
          : rowsHtml;
      }
      /* Mobile sheet list (same data) */
      if (this.boxListSheetEl) {
        this.boxListSheetEl.innerHTML = count === 0
          ? '<p class="sample-box-sheet__empty">Empty for now.</p>'
          : rowsHtml;
      }
      /* Wire all remove buttons across both surfaces */
      this.querySelectorAll('[data-remove-id]').forEach(btn => {
        if (btn._wvhWired) return;
        btn._wvhWired = true;
        btn.addEventListener('click', () => this._removeId(btn.dataset.removeId));
      });

      const countLabel = `${count} sample${count === 1 ? '' : 's'}`;
      if (this.boxCountEl) this.boxCountEl.textContent = countLabel;
      if (this.boxCountMobileEl) this.boxCountMobileEl.textContent = countLabel;
      if (this.boxCountSheetEl) this.boxCountSheetEl.textContent = countLabel;
      if (this.boxCountBadgeEl) this.boxCountBadgeEl.textContent = String(count);
      if (this.boxTotalEl) this.boxTotalEl.textContent = totalStr;
      if (this.boxTotalMobileEl) this.boxTotalMobileEl.textContent = totalStr;
      if (this.boxTotalSheetEl) this.boxTotalSheetEl.textContent = totalStr;

      if (this.boxCtaEl) this.boxCtaEl.disabled = count === 0;
      if (this.boxCtaSheetEl) this.boxCtaSheetEl.disabled = count === 0;
      if (this.boxClearEl) this.boxClearEl.hidden = count === 0;
      if (this.boxClearSheetEl) this.boxClearSheetEl.hidden = count === 0;
      if (this.boxBarEl) {
        this.boxBarEl.classList.toggle('is-visible', count > 0);
        /* Update aria-label so screen readers announce the current state
           when they read the bar — not just the static initial label. */
        if (this._barLabelTemplate) {
          this.boxBarEl.setAttribute(
            'aria-label',
            this._barLabelTemplate
              .replace('{count}', String(count))
              .replace('{total}', totalStr)
          );
        }
      }
      /* Page-level class so the sticky filter can offset its top to
         clear the mobile bar when present. */
      this.classList.toggle('has-bar-visible', count > 0);

      this._syncRailCtas();
    }

    /* ── A11y live announcements ──────────────────────────────────
       Polite-priority updates announce cart-state changes (add, remove,
       set added) without interrupting the customer's screen reader. */
    _announce(text) {
      if (!this.liveEl || !text) return;
      /* Force reflow so identical successive announcements are read again. */
      this.liveEl.textContent = '';
      requestAnimationFrame(() => { this.liveEl.textContent = text; });
    }

    _removeId(id) {
      this.selected.delete(id);
      const matched = this.querySelector(`[data-sample-card][data-variant-id="${id}"]`);
      if (matched) {
        matched.setAttribute('aria-pressed', 'false');
        matched.classList.remove('is-selected');
      }
      this._renderBox();
    }

    /* ── Box actions ──────────────────────────────────────────────────
       Selection is local-only. The CTA (mobile bar OR desktop right rail)
       is the moment we actually write to the cart: ALL selected variants
       posted in a single multi-line cart/add, then redirect straight to
       /checkout. The cart drawer is intentionally bypassed. */
    _wireBox() {
      if (this.boxCtaEl) {
        this.boxCtaEl.addEventListener('click', () => this._sendBox(this.boxCtaEl));
      }
      if (this.boxBarEl) {
        this.boxBarEl.addEventListener('click', () => this._sendBox(this.boxBarEl));
      }
      if (this.boxClearEl) {
        this.boxClearEl.addEventListener('click', () => this._clearAllSelected());
      }
    }

    _sendBox(triggerEl) {
      const items = Array.from(this.selected.values());
      if (!items.length) return;
      if (triggerEl) {
        triggerEl.classList.add('is-loading');
        triggerEl.setAttribute('aria-busy', 'true');
        if (triggerEl.tagName === 'BUTTON') triggerEl.disabled = true;
      }
      this._announce('Sending your sample box to checkout.');

      /* Native form POST to /cart/add with return_to=/checkout.
         Why a form, not fetch():
         - The fetch + window.location pattern races the cart cookie
           commit. The redirect can fire before Shopify finishes setting
           the session cart, leading to /checkout seeing an empty cart
           and silently falling back to /cart.
         - A form POST submits the page natively; Shopify's response IS
           the redirect (handled by the browser as a single navigation
           with the cart cookie already in place).
         - Multi-line cart add format documented by Shopify:
             items[0][id]=...&items[0][quantity]=1
             items[1][id]=...&items[1][quantity]=1
           with `return_to=/checkout` to navigate afterwards. */
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = this.cartAddUrl || '/cart/add';
      form.acceptCharset = 'UTF-8';
      form.style.display = 'none';

      items.forEach((item, i) => {
        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = `items[${i}][id]`;
        idInput.value = String(item.id);
        form.appendChild(idInput);

        const qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = `items[${i}][quantity]`;
        qtyInput.value = '1';
        form.appendChild(qtyInput);
      });

      const returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      returnTo.value = '/checkout';
      form.appendChild(returnTo);

      document.body.appendChild(form);
      form.submit();
    }

    _clearAllSelected() {
      this.selected.clear();
      this.querySelectorAll('[data-sample-card][aria-pressed="true"]').forEach(c => {
        c.setAttribute('aria-pressed', 'false');
        c.classList.remove('is-selected');
      });
      this._renderBox();
      this._announce('Sample box cleared.');
    }

    /* ── Visual: fly thumbnail to box panel ───────────────────────── */
    _flyToBox(sourceEl) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const target = this.querySelector('.sample-box-panel') || this.boxBarEl;
      if (!target) return;
      const sourceImg = sourceEl.querySelector('img');
      if (!sourceImg) return;
      const sRect = sourceImg.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();

      const clone = document.createElement('div');
      clone.className = 'sample-fly-clone';
      clone.style.left = sRect.left + 'px';
      clone.style.top = sRect.top + 'px';
      clone.style.width = sRect.width + 'px';
      clone.style.height = sRect.height + 'px';
      clone.style.setProperty('--fly-x', (tRect.left - sRect.left + 60) + 'px');
      clone.style.setProperty('--fly-y', (tRect.top - sRect.top + 30) + 'px');
      clone.innerHTML = `<img src="${sourceImg.src}" style="width:100%;height:100%;object-fit:cover;">`;
      document.body.appendChild(clone);
      setTimeout(() => clone.remove(), 700);
    }

    /* ── Helpers ──────────────────────────────────────────────────── */
    _money(cents) {
      const n = (cents || 0) / 100;
      return '£' + n.toFixed(2);
    }
    _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  customElements.define('sample-page', SamplePage);
})();
